//src/hooks/useRisks.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createRisk,
  deleteRisk,
  getRisk,
  linkActionPlanModelToRisk,
  listAvailableActionPlanModels,
  listRiskActionPlanModels,
  listRiskCategories,
  listRisks,
  setRiskStatus,
  unlinkActionPlanModelFromRisk,
  updateRisk,
} from "@/services/risks";
import type {
  RiskActionPlanModelLinkIn,
  RiskCatalogCreateIn,
  RiskCatalogUpdateIn,
  RiskTheme,
} from "@/types/risks";

export const RISK_CATEGORIES_QK = (params: {
  risk_theme?: RiskTheme | "";
  is_active?: boolean;
}) => ["risk_categories", params] as const;

export const RISKS_QK = (params: {
  q?: string;
  risk_category_id?: string;
  risk_theme?: RiskTheme | "";
  is_active?: boolean;
}) => ["risks", params] as const;

export const RISK_DETAIL_QK = (riskId: string | undefined) =>
  ["risk_detail", riskId] as const;

export const RISK_ACTION_PLAN_MODELS_QK = (riskId: string | undefined) =>
  ["risk_action_plan_models", riskId] as const;

export const AVAILABLE_ACTION_PLAN_MODELS_QK = (params: {
  q?: string;
  is_active?: boolean;
}) => ["available_action_plan_models", params] as const;

export function useRiskCategories(params: {
  risk_theme?: RiskTheme | "";
  is_active?: boolean;
}) {
  return useQuery({
    queryKey: RISK_CATEGORIES_QK(params),
    queryFn: () => listRiskCategories(params),
    staleTime: 60_000,
  });
}

export function useRisks(params: {
  q?: string;
  risk_category_id?: string;
  risk_theme?: RiskTheme | "";
  is_active?: boolean;
}) {
  return useQuery({
    queryKey: RISKS_QK(params),
    queryFn: () => listRisks(params),
    staleTime: 20_000,
  });
}

export function useRiskDetail(riskId: string | undefined) {
  return useQuery({
    queryKey: RISK_DETAIL_QK(riskId),
    queryFn: () => getRisk(riskId as string),
    enabled: !!riskId,
    staleTime: 20_000,
  });
}

export function useRiskActionPlanModels(riskId: string | undefined) {
  return useQuery({
    queryKey: RISK_ACTION_PLAN_MODELS_QK(riskId),
    queryFn: () => listRiskActionPlanModels(riskId as string),
    enabled: !!riskId,
    staleTime: 20_000,
  });
}

export function useAvailableActionPlanModels(
  riskId: string | undefined,
  params: { q?: string; is_active?: boolean },
) {
  return useQuery({
    queryKey: AVAILABLE_ACTION_PLAN_MODELS_QK(params),
    queryFn: () => listAvailableActionPlanModels(params),
    enabled: !!riskId,
    staleTime: 20_000,
  });
}

export function useCreateRisk() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: RiskCatalogCreateIn) => createRisk(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["risks"] });
    },
  });
}

export function useUpdateRisk() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      riskId,
      body,
    }: {
      riskId: string;
      body: RiskCatalogUpdateIn;
    }) => updateRisk(riskId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["risks"] });
      qc.invalidateQueries({ queryKey: RISK_DETAIL_QK(variables.riskId) });
    },
  });
}

export function useSetRiskStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      riskId,
      is_active,
    }: {
      riskId: string;
      is_active: boolean;
    }) => setRiskStatus(riskId, { is_active }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["risks"] });
      qc.invalidateQueries({ queryKey: RISK_DETAIL_QK(variables.riskId) });
    },
  });
}

export function useDeleteRisk() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (riskId: string) => deleteRisk(riskId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["risks"] });
    },
  });
}

export function useLinkActionPlanModelToRisk() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      riskId,
      body,
    }: {
      riskId: string;
      body: RiskActionPlanModelLinkIn;
    }) => linkActionPlanModelToRisk(riskId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: RISK_DETAIL_QK(variables.riskId) });
      qc.invalidateQueries({
        queryKey: RISK_ACTION_PLAN_MODELS_QK(variables.riskId),
      });
      qc.invalidateQueries({
        queryKey: AVAILABLE_ACTION_PLAN_MODELS_QK({ is_active: true }),
      });
    },
  });
}

export function useUnlinkActionPlanModelFromRisk() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      riskId,
      actionPlanModelId,
    }: {
      riskId: string;
      actionPlanModelId: string;
    }) => unlinkActionPlanModelFromRisk(riskId, actionPlanModelId),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: RISK_DETAIL_QK(variables.riskId) });
      qc.invalidateQueries({
        queryKey: RISK_ACTION_PLAN_MODELS_QK(variables.riskId),
      });
      qc.invalidateQueries({
        queryKey: AVAILABLE_ACTION_PLAN_MODELS_QK({ is_active: true }),
      });
    },
  });
}
